import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
import LoadsSummary from './LoadsSummary/loadsSummary';
import './dashboard.css';

interface NavCard {
    title: string;
    description: string;
    path: string;
    icon: string;
    category: string;
    requiresAccess?: string[];
    permissionLevel: number
}

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { hasAccAccess, hasAdminAccess } = useUserAccess();
    const { userRole = 'viewer' } = useUserAccess();

    const navigationCards: NavCard[] = [
        {
            title: 'Ticket Data',
            description: 'View and manage ticket records',
            path: '/ticketdata',
            icon: '📋',
            category: 'Operations',
            permissionLevel: 2
        },
        {
            title: 'Schedule Table',
            description: 'Schedule and manage crude, bottoms, and NGL loads',
            path: '/schedule',
            icon: '📅',
            category: 'Operations',
            permissionLevel: 1
        },
        {
            title: 'Map',
            description: 'View locations, routes, and coordinates',
            path: '/maps',
            icon: '🗺️',
            category: 'Operations',
            permissionLevel: 1
        },
        {
            title: 'Operations Tables',
            description: 'Manage operational reference tables',
            path: '/opstables',
            icon: '⚙️',
            category: 'Operations',
            permissionLevel: 1
        },
        // {
        //     title: 'MTD Summary',
        //     description: 'Month-to-date financial summary',
        //     path: '/mtdsummary',
        //     icon: '💰',
        //     category: 'Reports',
        //     requiresAccess: ['accounting', 'admin'],
        //     permissionLevel: 3
        // },
        {
            title: 'Approvals',
            description: 'Review and approve pending changes',
            path: '/approvals',
            icon: '✅',
            category: 'Admin',
            permissionLevel: 1
        },
        {
            title: 'PDF Approvals',
            description: 'Review and approve PDF tickets',
            path: '/pdfapprovals',
            icon: '📄',
            category: 'Admin',
            permissionLevel: 2
        },
        {
            title: 'Accounting Tables',
            description: 'Manage pricing and financial tables',
            path: '/acctables',
            icon: '💵',
            category: 'Accounting',
            requiresAccess: ['accounting', 'admin'],
            permissionLevel: 3
        },
        {
            title: 'Triggers',
            description: 'Manage automated triggers and alerts',
            path: '/triggers',
            icon: '⚡',
            category: 'Admin',
            requiresAccess: ['admin'],
            permissionLevel: 6
        },
        {
            title: 'Changelog',
            description: 'View system change history',
            path: '/changelog',
            icon: '📝',
            category: 'Admin',
            permissionLevel: 4
        },
        {
            title: 'Investigations',
            description: 'Audit logs and investigations',
            path: '/investigations',
            icon: '🔍',
            category: 'Admin',
            permissionLevel: 4
        },
        {
            title: 'Admin',
            description: 'System administration and settings',
            path: '/admin',
            icon: '🔧',
            category: 'Admin',
            requiresAccess: ['admin'],
            permissionLevel: 6
        }
    ];

    const hasAccessToCard = (card: NavCard): boolean => {
        if (!card.requiresAccess || card.requiresAccess.length === 0) {
            return true;
        }

        if (card.requiresAccess.includes('admin') && hasAdminAccess) {
            return true;
        }

        if (card.requiresAccess.includes('accounting') && (hasAccAccess || hasAdminAccess)) {
            return true;
        }

        return false;
    };

    const filteredCards = navigationCards.filter(hasAccessToCard);

    const categories = Array.from(new Set(filteredCards.map(card => card.category)));

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1 className="dashboard-title">Rockbridge Petroleum Web App</h1>
                <p className="dashboard-subtitle">Navigate to your destination</p>
            </div>

            <LoadsSummary />

            {categories.map(category => (
                <div key={category} className="nav-category">
                    <h2 className="category-title">{category}</h2>
                    <div className="nav-grid">
                        {filteredCards
                            .filter(card =>
                                card.category === category &&
                                card.permissionLevel <= USER_ROLE_LEVEL[userRole]
                            )
                            .map(card => (
                                <div
                                    key={card.path}
                                    className="nav-card"
                                    onClick={() => navigate(card.path)}
                                >
                                    <div className="nav-card-icon">{card.icon}</div>
                                    <div className="nav-card-content">
                                        <h3 className="nav-card-title">{card.title}</h3>
                                        <p className="nav-card-description">{card.description}</p>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Dashboard;
